/**
 * Builds a closed, slightly hand-wobbled rounded-rectangle SVG path `d` string.
 *
 * Used by the World Cup "Rules" tutorial (Design B — hand-drawn skin) to ink a
 * sketchy border around each example card. The wobble is baked into a computed
 * `<Path>` rather than an SVG turbulence/displacement filter on purpose: filters
 * render on web but are unreliable / silently no-op on native (iOS/Android), so a
 * perturbed path is the only approach that looks identical on every platform.
 *
 * The perturbation is deterministic (a sum of sines keyed on `seed`) so the same
 * inputs always produce the same border — no per-render flicker — and periodic in
 * the perimeter parameter so the closing point lands exactly on the start (no seam).
 */
export interface SketchBorderPathOptions {
  width: number;
  height: number;
  /** Corner radius of the underlying rounded rectangle. */
  radius?: number;
  /** Number of sample points walked around the perimeter. */
  steps?: number;
  /** Maximum perpendicular wobble, in px. */
  wobble?: number;
  /** Inset from the edge so the wobble stays inside the SVG viewport. */
  inset?: number;
  /** Varies the wobble pattern (use a different value per overlaid stroke). */
  seed?: number;
}

interface PerimeterSample {
  x: number;
  y: number;
  /** Outward unit normal at this point. */
  nx: number;
  ny: number;
}

const TAU = Math.PI * 2;

/** Smooth, periodic pseudo-noise in roughly [-1, 1] for t in [0, 1]. */
const wobbleNoise = (t: number, seed: number): number =>
  Math.sin(t * TAU * 3 + seed) * 0.6 + Math.sin(t * TAU * 7 + seed * 1.7) * 0.4;

/** Samples the rounded-rectangle perimeter into evenly spaced points with normals. */
const sampleRoundedRect = (
  x0: number,
  y0: number,
  w: number,
  h: number,
  r: number,
  steps: number,
): PerimeterSample[] => {
  const right = x0 + w;
  const bottom = y0 + h;
  // Straight-edge spans and quarter-circle arc length, walked clockwise from the
  // start of the top edge. Lengths drive even spacing so wobble reads uniform.
  const edgeTop = w - 2 * r;
  const edgeSide = h - 2 * r;
  const arc = (TAU / 4) * r;
  const perimeter = 2 * edgeTop + 2 * edgeSide + 4 * arc;

  const segments: Array<{ len: number; at: (u: number) => PerimeterSample }> = [
    // Top edge: left → right, normal up.
    { len: edgeTop, at: (u) => ({ x: x0 + r + u, y: y0, nx: 0, ny: -1 }) },
    // Top-right corner arc.
    {
      len: arc,
      at: (u) => {
        const a = -Math.PI / 2 + (u / arc) * (Math.PI / 2);
        return { x: right - r + Math.cos(a) * r, y: y0 + r + Math.sin(a) * r, nx: Math.cos(a), ny: Math.sin(a) };
      },
    },
    // Right edge: top → bottom, normal right.
    { len: edgeSide, at: (u) => ({ x: right, y: y0 + r + u, nx: 1, ny: 0 }) },
    // Bottom-right corner arc.
    {
      len: arc,
      at: (u) => {
        const a = (u / arc) * (Math.PI / 2);
        return { x: right - r + Math.cos(a) * r, y: bottom - r + Math.sin(a) * r, nx: Math.cos(a), ny: Math.sin(a) };
      },
    },
    // Bottom edge: right → left, normal down.
    { len: edgeTop, at: (u) => ({ x: right - r - u, y: bottom, nx: 0, ny: 1 }) },
    // Bottom-left corner arc.
    {
      len: arc,
      at: (u) => {
        const a = Math.PI / 2 + (u / arc) * (Math.PI / 2);
        return { x: x0 + r + Math.cos(a) * r, y: bottom - r + Math.sin(a) * r, nx: Math.cos(a), ny: Math.sin(a) };
      },
    },
    // Left edge: bottom → top, normal left.
    { len: edgeSide, at: (u) => ({ x: x0, y: bottom - r - u, nx: -1, ny: 0 }) },
    // Top-left corner arc.
    {
      len: arc,
      at: (u) => {
        const a = Math.PI + (u / arc) * (Math.PI / 2);
        return { x: x0 + r + Math.cos(a) * r, y: y0 + r + Math.sin(a) * r, nx: Math.cos(a), ny: Math.sin(a) };
      },
    },
  ];

  const samples: PerimeterSample[] = [];
  for (let i = 0; i < steps; i += 1) {
    let dist = (i / steps) * perimeter;
    for (const seg of segments) {
      if (dist <= seg.len || seg === segments[segments.length - 1]) {
        samples.push(seg.at(Math.max(0, Math.min(seg.len, dist))));
        break;
      }
      dist -= seg.len;
    }
  }
  return samples;
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

export const buildSketchBorderPath = ({
  width,
  height,
  radius = 24,
  steps = 96,
  wobble = 1.6,
  inset,
  seed = 1,
}: SketchBorderPathOptions): string => {
  if (width <= 0 || height <= 0) {
    return '';
  }
  const margin = inset ?? wobble + 2;
  const w = Math.max(0, width - 2 * margin);
  const h = Math.max(0, height - 2 * margin);
  // Clamp the radius so it never exceeds half of the shorter inset side.
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));

  const samples = sampleRoundedRect(margin, margin, w, h, r, steps);
  if (samples.length === 0) {
    return '';
  }

  const points = samples.map((p, i) => {
    const t = i / samples.length;
    const offset = wobbleNoise(t, seed) * wobble;
    return { x: round2(p.x + p.nx * offset), y: round2(p.y + p.ny * offset) };
  });

  const [first, ...rest] = points;
  const body = rest.map((p) => `L ${p.x} ${p.y}`).join(' ');
  return `M ${first.x} ${first.y} ${body} Z`;
};
